import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import { InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { GetInvoiceListParams } from '@nxsys/types/api';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import { hasFilterValue } from '@nxsys/utils/filterForm';

const REPORT_BY_INVOICE_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'invoiceNo', label: 'Invoice No' },
  { value: 'total', label: 'Total' },
];

const REPORT_BY_CREDIT_NOTE_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'creditNo', label: 'Credit No' },
  { value: 'total', label: 'Total' },
];

type AgencyFilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: GetInvoiceListParams) => void;
  invoiceType?: InvoiceTypeEnum;
};

type FormValues = {
  reportBy: string[];
  startDate?: string;
  endDate?: string;
  invoiceNo?: string;
  creditNo?: string;
  totalPrices?: string;
};

const AgencyFilterModal: React.FC<AgencyFilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
  invoiceType = InvoiceTypeEnum.INVOICE,
}: AgencyFilterModalProps) => {
  const { messages } = useTypedIntl();

  const isCreditNote = invoiceType === InvoiceTypeEnum.CREDIT_NOTE;
  const numberFieldKey = isCreditNote ? 'creditNo' : 'invoiceNo';

  const reportByOptions = useMemo(
    () =>
      isCreditNote ? REPORT_BY_CREDIT_NOTE_OPTIONS : REPORT_BY_INVOICE_OPTIONS,
    [isCreditNote],
  );

  const [form] = Form.useForm<FormValues>();
  const reportBy = Form.useWatch('reportBy', form);
  const watchValue = Form.useWatch([], form);

  const selectedReportBy = (reportBy ?? []) as string[];

  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  // When reportBy changes, clear values for unchecked items
  useEffect(() => {
    const updates: Partial<FormValues> = {};

    if (!selectedReportBy.includes('date')) {
      updates.startDate = undefined;
      updates.endDate = undefined;
    }
    if (!selectedReportBy.includes(numberFieldKey)) {
      updates.invoiceNo = undefined;
      updates.creditNo = undefined;
    }
    if (!selectedReportBy.includes('total')) {
      updates.totalPrices = undefined;
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [selectedReportBy.join(','), numberFieldKey, form]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values: FormValues) => {
    const params: GetInvoiceListParams = {};

    if (selectedReportBy.includes('date')) {
      if (values.startDate)
        params.startDate = moment(values.startDate).toISOString();
      if (values.endDate) params.endDate = moment(values.endDate).toISOString();
    }
    if (isCreditNote) {
      if (selectedReportBy.includes('creditNo') && values.creditNo)
        params.creditNo = values.creditNo;
    } else {
      if (selectedReportBy.includes('invoiceNo') && values.invoiceNo)
        params.invoiceNo = values.invoiceNo;
    }
    if (selectedReportBy.includes('total') && values.totalPrices)
      params.totalPrices = values.totalPrices;
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // noop - form validation handles error display
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          name="reportBy"
          label={messages['invoices.reportBy'] ?? 'Report By'}
          initialValue={[]}
        >
          <StyledAppSelect
            mode="multiple"
            options={[...reportByOptions]}
            placeholder={messages['form.pleaseSelect']}
            size="large"
            allowClear
          />
        </Form.Item>

        {selectedReportBy.includes('date') && (
          <div className="flex gap-x-4" key="date-fields">
            <Form.Item
              label={messages['common.from']}
              name="startDate"
              className="w-1/2"
            >
              <AppDatePicker
                placeholder={messages['common.selectDate']}
                onChange={(date: Moment) => {
                  form.setFieldsValue({
                    startDate: date?.toISOString(),
                  });
                }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
            <Form.Item
              label={messages['common.to']}
              name="endDate"
              className="w-1/2"
            >
              <AppDatePicker
                placeholder={messages['common.selectDate']}
                onChange={(date: Moment) => {
                  form.setFieldsValue({
                    endDate: date?.toISOString(),
                  });
                }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </div>
        )}

        {selectedReportBy.includes(numberFieldKey) && (
          <Form.Item
            key={numberFieldKey}
            name={numberFieldKey}
            label={
              isCreditNote
                ? messages['invoices.creditNo']
                : messages['invoices.invoiceNumber']
            }
          >
            <AppInput placeholder={messages['form.pleaseEnter']} />
          </Form.Item>
        )}

        {selectedReportBy.includes('total') && (
          <Form.Item
            key="totalPrices"
            name="totalPrices"
            label={messages['common.total']}
          >
            <AppCurrencyInput
              placeholder={messages['form.pleaseEnter']}
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default AgencyFilterModal;
